import { createStorefrontApiClient } from '@shopify/storefront-api-client';

// Shopify Storefront API Configuration
const SHOPIFY_STORE_DOMAIN = 'zk8n0h-1h.myshopify.com';
const STOREFRONT_ACCESS_TOKEN = 'b86419c086d8378ae6d10b8d2acb9896';
const API_VERSION = '2025-01';

// Create the Storefront API client
export const storefrontClient = createStorefrontApiClient({
  storeDomain: SHOPIFY_STORE_DOMAIN,
  apiVersion: API_VERSION,
  publicAccessToken: STOREFRONT_ACCESS_TOKEN,
});

// GraphQL query to fetch all products with tags
const PRODUCTS_QUERY = `
  query getProducts($first: Int!) {
    products(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          descriptionHtml
          onlineStoreUrl
          tags
          productType
          images(first: 3) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 5) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// GraphQL query to fetch shop information
const SHOP_QUERY = `
  query {
    shop {
      name
      description
      primaryDomain {
        url
      }
      brand {
        shortDescription
        slogan
      }
    }
  }
`;

// GraphQL query to fetch all collections (categories)
const COLLECTIONS_QUERY = `
  query getCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          image {
            url
            altText
          }
          products(first: 100) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;

// GraphQL mutation to create a cart
const CREATE_CART_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL mutation to add items to cart
const ADD_TO_CART_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL mutation to update cart lines
const UPDATE_CART_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL mutation to remove cart lines
const REMOVE_FROM_CART_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL query to fetch cart
const GET_CART_QUERY = `
  query getCart($cartId: ID!) {
    cart(id: $cartId) {
      id
      checkoutUrl
      totalQuantity
      cost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
      }
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                price {
                  amount
                  currencyCode
                }
                product {
                  title
                  handle
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Fetch all products from Shopify
export const fetchProducts = async (limit = 50) => {
  try {
    const response = await storefrontClient.request(PRODUCTS_QUERY, {
      variables: { first: limit },
    });

    if (response.errors) {
      console.error('Shopify API errors:', response.errors);
      return { products: [], tags: [], productTypes: [] };
    }

    const allTags = new Set();
    const allProductTypes = new Set();

    const products = response.data?.products?.edges?.map((edge) => {
      const product = edge.node;
      const firstImage = product.images?.edges?.[0]?.node;
      const firstVariant = product.variants?.edges?.[0]?.node;
      
      // Collect tags
      if (product.tags && product.tags.length > 0) {
        product.tags.forEach(tag => allTags.add(tag));
      }
      
      // Collect product types
      if (product.productType) {
        allProductTypes.add(product.productType);
      }
      
      // Build the product URL - use onlineStoreUrl if available, otherwise construct it
      const productUrl = product.onlineStoreUrl || 
        `https://pureintentionalmt.com/products/${product.handle}`;

      return {
        id: product.id,
        name: product.title,
        handle: product.handle,
        description: product.description || '',
        descriptionHtml: product.descriptionHtml || '',
        image: firstImage?.url || '/images/placeholder.jpg',
        imageAlt: firstImage?.altText || product.title,
        price: firstVariant?.price?.amount 
          ? `$${parseFloat(firstVariant.price.amount).toFixed(2)}`
          : 'Price unavailable',
        priceAmount: firstVariant?.price?.amount || 0,
        currency: firstVariant?.price?.currencyCode || 'USD',
        availableForSale: firstVariant?.availableForSale ?? true,
        url: productUrl,
        tags: product.tags || [],
        productType: product.productType || '',
        variantId: firstVariant?.id || null,
        variants: product.variants?.edges?.map((v) => ({
          id: v.node.id,
          title: v.node.title,
          price: v.node.price?.amount,
          currency: v.node.price?.currencyCode,
          availableForSale: v.node.availableForSale,
        })) || [],
        allImages: product.images?.edges?.map((img) => ({
          url: img.node.url,
          altText: img.node.altText,
        })) || [],
      };
    }) || [];

    return {
      products,
      tags: Array.from(allTags).sort(),
      productTypes: Array.from(allProductTypes).sort(),
    };
  } catch (error) {
    console.error('Error fetching products from Shopify:', error);
    return { products: [], tags: [], productTypes: [] };
  }
};

// Fetch shop information
export const fetchShopInfo = async () => {
  try {
    const response = await storefrontClient.request(SHOP_QUERY);

    if (response.errors) {
      console.error('Shopify API errors:', response.errors);
      return null;
    }

    return response.data?.shop || null;
  } catch (error) {
    console.error('Error fetching shop info from Shopify:', error);
    return null;
  }
};

// Fetch all collections (categories) from Shopify
export const fetchCollections = async (limit = 50) => {
  try {
    const response = await storefrontClient.request(COLLECTIONS_QUERY, {
      variables: { first: limit },
    });

    if (response.errors) {
      console.error('Shopify API errors:', response.errors);
      return [];
    }

    const collections = response.data?.collections?.edges?.map((edge) => {
      const collection = edge.node;
      const productIds = collection.products?.edges?.map(p => p.node.id) || [];
      
      return {
        id: collection.id,
        title: collection.title,
        handle: collection.handle,
        description: collection.description || '',
        image: collection.image?.url || null,
        imageAlt: collection.image?.altText || collection.title,
        productIds: productIds,
        productCount: productIds.length,
      };
    }) || [];

    // Filter out empty collections and sort by title
    return collections
      .filter(c => c.productCount > 0)
      .sort((a, b) => a.title.localeCompare(b.title));
  } catch (error) {
    console.error('Error fetching collections from Shopify:', error);
    return [];
  }
};

// Create a new cart
export const createCart = async (lines = []) => {
  try {
    const response = await storefrontClient.request(CREATE_CART_MUTATION, {
      variables: {
        input: {
          lines: lines,
        },
      },
    });

    if (response.errors || response.data?.cartCreate?.userErrors?.length > 0) {
      console.error('Cart creation errors:', response.errors || response.data?.cartCreate?.userErrors);
      return null;
    }

    return response.data?.cartCreate?.cart;
  } catch (error) {
    console.error('Error creating cart:', error);
    return null;
  }
};

// Add items to cart
export const addToCart = async (cartId, lines) => {
  try {
    const response = await storefrontClient.request(ADD_TO_CART_MUTATION, {
      variables: {
        cartId,
        lines,
      },
    });

    if (response.errors || response.data?.cartLinesAdd?.userErrors?.length > 0) {
      console.error('Add to cart errors:', response.errors || response.data?.cartLinesAdd?.userErrors);
      return null;
    }

    return response.data?.cartLinesAdd?.cart;
  } catch (error) {
    console.error('Error adding to cart:', error);
    return null;
  }
};

// Update cart lines
export const updateCartLines = async (cartId, lines) => {
  try {
    const response = await storefrontClient.request(UPDATE_CART_MUTATION, {
      variables: {
        cartId,
        lines,
      },
    });

    if (response.errors || response.data?.cartLinesUpdate?.userErrors?.length > 0) {
      console.error('Update cart errors:', response.errors || response.data?.cartLinesUpdate?.userErrors);
      return null;
    }

    return response.data?.cartLinesUpdate?.cart;
  } catch (error) {
    console.error('Error updating cart:', error);
    return null;
  }
};

// Remove items from cart
export const removeFromCart = async (cartId, lineIds) => {
  try {
    const response = await storefrontClient.request(REMOVE_FROM_CART_MUTATION, {
      variables: {
        cartId,
        lineIds,
      },
    });

    if (response.errors || response.data?.cartLinesRemove?.userErrors?.length > 0) {
      console.error('Remove from cart errors:', response.errors || response.data?.cartLinesRemove?.userErrors);
      return null;
    }

    return response.data?.cartLinesRemove?.cart;
  } catch (error) {
    console.error('Error removing from cart:', error);
    return null;
  }
};

// Get cart by ID
export const getCart = async (cartId) => {
  try {
    const response = await storefrontClient.request(GET_CART_QUERY, {
      variables: { cartId },
    });

    if (response.errors) {
      console.error('Get cart errors:', response.errors);
      return null;
    }

    return response.data?.cart;
  } catch (error) {
    console.error('Error getting cart:', error);
    return null;
  }
};

export default {
  fetchProducts,
  fetchShopInfo,
  storefrontClient,
  createCart,
  addToCart,
  updateCartLines,
  removeFromCart,
  getCart,
};
